import {useI18n} from 'i18n/I18nContext'
import React, {useEffect} from 'react'
import styled from 'styled-components'
import {SliderInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/SliderInput'
import {Padding} from 'TemplateEditor/Layout/Blocks/DefaultBlockConfig'

const PADDING_SLIDER_STEP = 5

function snapToStep(n: number, min: number, max: number, step: number): number {
  if (!Number.isFinite(n)) return min
  const snapped = Math.round((n - min) / step) * step + min
  return Math.min(max, Math.max(min, snapped))
}

export function PaddingInput(props: {
  value: Padding
  setFieldValue: (field: string, value: Padding) => void
  label: string
  name: string
  max: number
  min: number
}) {
  const {t} = useI18n()
  const {value, setFieldValue, name, max, min} = props

  const patch = (partial: Partial<Padding>) => {
    setFieldValue(name, {...value, ...partial})
  }

  useEffect(() => {
    const snapped = (k: keyof Padding) =>
      snapToStep(value[k], min, max, PADDING_SLIDER_STEP)
    const next: Padding = {
      top: snapped('top'),
      left: snapped('left'),
      right: snapped('right'),
      bottom: snapped('bottom'),
    }
    if (
      next.top === value.top &&
      next.left === value.left &&
      next.right === value.right &&
      next.bottom === value.bottom
    ) {
      return
    }
    setFieldValue(name, next)
  }, [
    value.top,
    value.left,
    value.right,
    value.bottom,
    min,
    max,
    name,
    setFieldValue,
  ])

  const edgeSetter = (key: keyof Padding) => (_sub: string, num: number) => {
    patch({[key]: snapToStep(num, min, max, PADDING_SLIDER_STEP)})
  }

  return (
    <PaddingSliderGrid>
      <SliderInput
        value={snapToStep(value.top, min, max, PADDING_SLIDER_STEP)}
        name={`${name}.__top`}
        label={t('templateEditor.paddingEdgeTop')}
        setFieldValue={edgeSetter('top')}
        max={max}
        min={min}
        step={PADDING_SLIDER_STEP}
        compact
      />
      <SliderInput
        value={snapToStep(value.left, min, max, PADDING_SLIDER_STEP)}
        name={`${name}.__left`}
        label={t('templateEditor.paddingEdgeLeft')}
        setFieldValue={edgeSetter('left')}
        max={max}
        min={min}
        step={PADDING_SLIDER_STEP}
        compact
      />
      <SliderInput
        value={snapToStep(value.bottom, min, max, PADDING_SLIDER_STEP)}
        name={`${name}.__bottom`}
        label={t('templateEditor.paddingEdgeBottom')}
        setFieldValue={edgeSetter('bottom')}
        max={max}
        min={min}
        step={PADDING_SLIDER_STEP}
        compact
      />
      <SliderInput
        value={snapToStep(value.right, min, max, PADDING_SLIDER_STEP)}
        name={`${name}.__right`}
        label={t('templateEditor.paddingEdgeRight')}
        setFieldValue={edgeSetter('right')}
        max={max}
        min={min}
        step={PADDING_SLIDER_STEP}
        compact
      />
    </PaddingSliderGrid>
  )
}

/** Two columns × two rows: less vertical space than a single column of four. */
const PaddingSliderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
  row-gap: 2px;
  width: 100%;
  max-width: 100%;
  margin-bottom: 0.35rem;
`
